import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import FileUploadDropzone from "../../components/FileUploadDropzone";
import { toast, Toaster } from "sonner";
import { FilePlus, Loader2, Zap } from "lucide-react";

const CLAIM_TYPE_KEYS = [
  "mpesa_fraud",
  "account_takeover",
  "phishing_loss",
  "cyber_extortion",
  "pos_settlement_fraud",
  "whatsapp_business_compromise",
];

export default function FileClaim() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState({
    policy_id: "", claim_type: "mpesa_fraud", amount_claimed: "",
    description: "", country: "KE",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/merchant/me").then((r) => {
      const active = r.data.policies.filter((p) => ["active", "cooling_off"].includes(p.status));
      setPolicies(active);
      if (active[0]) setForm((f) => ({ ...f, policy_id: active[0].id }));
    });
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        merchant_id: user.merchant_id,
        policy_id: form.policy_id,
        claim_type: form.claim_type,
        amount_claimed: parseInt(form.amount_claimed, 10),
        description: form.description,
        country: form.country,
        evidence_urls: files.map((f) => f.url),
      };
      const { data } = await api.post("/merchant/claims", payload);
      toast.success(`${data.claim_number} — ${data.status.replace(/_/g, " ")}`);
      setTimeout(() => nav("/merchant"), 1200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-8 max-w-2xl space-y-6" data-testid="file-claim-page">
      <Toaster position="top-right" />
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5c6360] flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-[#c15337]" /> {t("file_claim.submit")}
        </div>
        <h1 className="font-heading text-3xl font-bold mt-1 flex items-center gap-2 text-[#114232]">
          <FilePlus className="w-6 h-6" /> {t("file_claim.title")}
        </h1>
        <p className="text-sm text-[#5c6360] mt-1">{t("file_claim.subtitle")}</p>
      </header>

      <form
        onSubmit={onSubmit}
        className="bg-white border border-[#114232]/10 rounded-lg p-6 space-y-5"
        data-testid="claim-form"
      >
        <div className="space-y-2">
          <Label htmlFor="policy">{t("file_claim.policy")}</Label>
          <select
            id="policy" required value={form.policy_id}
            onChange={(e) => setForm((f) => ({ ...f, policy_id: e.target.value }))}
            data-testid="claim-policy-select"
            className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#114232]/20"
          >
            {policies.length === 0 && <option value="">{t("file_claim.no_active_policy")}</option>}
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.policy_number} — {t("file_claim.cover")} KES {p.cover_limit.toLocaleString()}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="type">{t("file_claim.incident_type")}</Label>
          <select
            id="type" required value={form.claim_type}
            onChange={(e) => setForm((f) => ({ ...f, claim_type: e.target.value }))}
            data-testid="claim-type-select"
            className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#114232]/20"
          >
            {CLAIM_TYPE_KEYS.map((k) => (
              <option key={k} value={k}>{t(`file_claim.claim_types.${k}`)}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="amount">{t("file_claim.amount_claimed")}</Label>
          <Input
            id="amount" type="number" min="100" required
            value={form.amount_claimed}
            onChange={(e) => setForm((f) => ({ ...f, amount_claimed: e.target.value }))}
            data-testid="claim-amount-input"
            placeholder="8500"
            className="border-[#114232]/15"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="desc">{t("file_claim.what_happened")}</Label>
          <Textarea
            id="desc" rows={4} required
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            data-testid="claim-description-input"
            placeholder={t("file_claim.incident_placeholder")}
            className="border-[#114232]/15"
          />
        </div>

        <div className="space-y-2">
          <Label>{t("file_claim.evidence")}</Label>
          <FileUploadDropzone files={files} setFiles={setFiles} purpose="claim_evidence" />
        </div>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => nav("/merchant")}
            className="text-sm text-[#5c6360] hover:text-[#114232] transition-colors"
          >
            {t("common.cancel")}
          </button>
          <Button
            type="submit"
            disabled={submitting || !form.policy_id}
            data-testid="claim-submit-btn"
            className="btn-terracotta rounded-full px-5 hover:bg-[var(--terracotta-hover)]"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("file_claim.submit_claim")}
          </Button>
        </div>
      </form>
    </div>
  );
}
